import { useState, type KeyboardEvent } from 'react'
import { PlusIcon, XIcon } from '../icons'

interface Props {
  /** The tags as they stand. Order is kept, it is the order they appear in. */
  value: string[]
  onChange: (next: string[]) => void
  /** What a single entry is, for the empty state and the add field. */
  placeholder?: string
  /** Shown in place of the list when there is nothing in it. */
  emptyText?: string
  /** Kutuda yazılı ama henüz eklenmemiş yazı: pencere "Kaydet"te onu da alsın. */
  onPendingChange?: (entry: string) => void
}

/**
 * A list of short strings, added one at a time and removed with a cross.
 *
 * Replaces asking for the same list as comma-separated text. That form made
 * the separator part of the vocabulary, a note could never itself contain a
 * comma, and it put the burden of formatting on the person typing, who had
 * to be told about it in a paragraph of guidance above the field. A tag that
 * is already on the list is also visibly on the list, which is what stops the
 * same note being entered twice.
 */
export function TagListEditor({ value, onChange, placeholder, emptyText, onPendingChange }: Props) {
  const [entry, setEntryState] = useState('')
  const setEntry = (v: string) => {
    setEntryState(v)
    onPendingChange?.(v)
  }

  const trimmed = entry.trim()
  // Case-insensitive against the Turkish alphabet: "acılı" and "Acılı" are one
  // note, and i/İ is exactly where a naive comparison gets it wrong.
  const duplicate = value.some(
    (v) => v.toLocaleLowerCase('tr-TR') === trimmed.toLocaleLowerCase('tr-TR'),
  )
  const canAdd = trimmed !== '' && !duplicate

  function add() {
    if (!canAdd) return
    onChange([...value, trimmed])
    setEntry('')
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // Enter adds without reaching for the button, which is how a list of five
    // gets typed. Deliberately swallowed so it cannot submit a form this sits
    // inside, the product form is one.
    if (e.key === 'Enter') {
      e.preventDefault()
      add()
    }
  }

  return (
    <div className="tags">
      <div className="tags__add">
        <input
          className="tags__input"
          value={entry}
          onChange={(e) => setEntry(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? 'Soğansız'}
          autoComplete="off"
        />
        <button
          type="button"
          className="tags__plus"
          onClick={add}
          disabled={!canAdd}
          title={duplicate ? 'Bu seçenek zaten var' : 'Ekle'}
          aria-label="Ekle"
        >
          <PlusIcon />
          <span>Ekle</span>
        </button>
      </div>

      {value.length === 0 ? (
        emptyText !== '' && <p className="tags__empty">{emptyText ?? 'Henüz seçenek yok.'}</p>
      ) : (
        <ul className="tags__list">
          {value.map((tag) => (
            <li key={tag} className="tags__tag">
              <span className="tags__text">{tag}</span>
              <button
                type="button"
                className="tags__drop"
                onClick={() => onChange(value.filter((v) => v !== tag))}
                title={`"${tag}" seçeneğini kaldır`}
                aria-label={`"${tag}" seçeneğini kaldır`}
              >
                <XIcon />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
